import * as React from 'react';

import Checkbox from 'components/Checkbox';
import TextWithSubtext from 'components/TextWithSubtext';
import Row from 'components/Row';
import Image from 'components/Image';
import MediaObject from 'components/MediaObject';
import TeacherImage from 'assets/images/roles/teacher.svg';
import StudentImage from 'assets/images/roles/student.svg';
import ParentImage from 'assets/images/roles/parent.svg';
import NoRoleImage from 'assets/images/roles/no-role.svg';
import StaffImage from 'assets/images/roles/staff.svg';
import EmployeeImage from 'assets/images/roles/employee.svg';
import LinkBase from 'components/LinkBase';
import { OrganizationRole } from 'globalTypes';
import { capitalize, snakeCase } from 'lodash';
import LinkConstants from 'constants/LinkConstants';
import Link from 'components/Link';

import './styles/OrgRoleSelection.scss';

export type Props = {
  selectedRoles: OrganizationRole[];
  availableRoles: OrganizationRole[];
  onChange: (newSelectedRoles: OrganizationRole[], noRoleSelected: boolean) => void;
  showNoRole: boolean;
  noRoleSelected: boolean;
};

export default class OrgRoleSelection extends React.Component<Props> {
  imageForRole(role: OrganizationRole) {
    switch (role) {
      case OrganizationRole.teacher:
        return TeacherImage;
      case OrganizationRole.parent:
        return ParentImage;
      case OrganizationRole.student:
        return StudentImage;
      case OrganizationRole.staff:
        return StaffImage;
      case OrganizationRole.employee:
        return EmployeeImage;
      default:
        return '';
    }
  }

  descriptionForRole(role: OrganizationRole) {
    switch (role) {
      case OrganizationRole.teacher:
        return 'Or coach, club adviser, organizer, etc.';
      case OrganizationRole.parent:
        return 'Or guardian';
      case OrganizationRole.student:
        return 'Or member of a sports team or club, etc.';
      case OrganizationRole.staff:
        return 'Or any non-teacher who needs to message students';
      case OrganizationRole.employee:
        return 'Or any non-teacher who needs to receive messages';
      default:
        return '';
    }
  }

  renderRole(
    role: string,
    selected: boolean,
    image: string,
    description: string | React.ReactElement,
    onClick: () => void
  ) {
    const key = snakeCase(role.toLowerCase());
    return (
      <Row className="OrgRoleSelectionlRoleDescription" flush key={role}>
        <LinkBase data-test={`${key}-row`} onClick={onClick}>
          <MediaObject center gutter="small">
            <Image src={image} width={32} />
            <MediaObject figure="right" center>
              <TextWithSubtext align="left" subtext={description}>
                {role}
              </TextWithSubtext>
              <Checkbox checked={selected} readOnly data-test={`${key}-checkbox`} />
            </MediaObject>
          </MediaObject>
        </LinkBase>
      </Row>
    );
  }

  render() {
    const { selectedRoles, onChange } = this.props;

    return (
      <>
        {this.props.availableRoles.map((role) => {
          const selected = this.props.selectedRoles.includes(role);
          return this.renderRole(
            capitalize(role),
            selected,
            this.imageForRole(role),
            this.descriptionForRole(role),
            () => {
              if (selectedRoles.includes(role)) {
                onChange(
                  selectedRoles.filter((r) => r != role),
                  false
                );
              } else {
                if (role === OrganizationRole.student) {
                  onChange([OrganizationRole.student], false);
                } else {
                  onChange(
                    [role, ...selectedRoles.filter((r) => r !== OrganizationRole.student)],
                    false
                  );
                }
              }
            }
          );
        })}
        {this.props.showNoRole &&
          this.renderRole(
            'Not sure',
            this.props.noRoleSelected,
            NoRoleImage,
            <>
              Notify users to update their own role.{' '}
              <Link
                onClick={(event) => {
                  event.stopPropagation();
                  window.open(LinkConstants.SELF_ROLE_CHANGE, '_blank');
                }}
              >
                Learn more
              </Link>
            </>,
            () => this.props.onChange([], !this.props.noRoleSelected)
          )}
      </>
    );
  }
}
